import { createContext, useEffect, useReducer } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import axios from "axios";
import { axiosApiInstance, axiosApiInstanceAuth } from "app/Api/axios";
import { LOGIN_URL, ADMIN_LOGIN_URL } from "app/Api/ApiEndPoint";
import { getCurrentRole } from "app/services/Services";
import { clearLoggedInUserInvalidCredential } from "app/utils/utils";
import { generateErrorMessage } from "app/utils/GenerateMessage";

// Initial state
const initialState = {
  user: null,
  isInitialised: false,
  isAuthenticated: false,
  role: null,
  checkIn: localStorage.getItem("checkIn") ?? "false",
  checkOut: localStorage.getItem("checkOut") ?? "true",
  userName: "",
  error: null,
};

// Reducer function
const reducer = (state, action) => {
  switch (action.type) {
    case "INIT":
      return { ...state, isAuthenticated: action.payload.isAuthenticated, isInitialised: true, user: action.payload.user };
      
    case "LOGIN":
      return { 
        ...state, 
        isAuthenticated: true, 
        user: action.payload.user, 
        role: action.payload.role, 
        userName: action.payload.user?.employeeData?.firstName,
        error: null 
      };

    case "LOGOUT":
      return { ...initialState, isInitialised: true };

    case "REFRESH_ROLE":
      return { ...state, role: action.payload.role };

    case "AUTH_ERROR":
      return { ...state, error: action.payload.error, isAuthenticated: false };

    case "UPDATE_USER_NAME":
      return { ...state, userName: action.payload.userName };

    case "CHECK_IN":
      return { ...state, checkIn: action.payload.checkIn };

    case "CHECK_OUT":
      return { ...state, checkOut: action.payload.checkOut };

    default:
      return state;
  }
};

// Create AuthContext
const AuthContext = createContext({
  ...initialState,
  method: "JWT",
  login: () => {},
  logout: () => {},
  register: () => {},
  checkIn: () => {},
  checkOut: () => {},
});

// AuthProvider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const navigate = useNavigate();
  const location = useLocation();

  // Login function
  const login = async (userId, userPassword) => {
    try {
      const response = await axiosApiInstanceAuth.post(LOGIN_URL, { userId, userPassword });
      
      if (response?.data) {
        const { data: user, roleData: role } = response.data;
        
        // Store token and expiration
        if (user?.tokenData) {
          localStorage.setItem("tokan", user.tokenData);
          if (user?.expiresUnix) {
            const expirationTime = new Date(parseInt(user.expiresUnix) * 1000);
            localStorage.setItem("tokenExpiration", expirationTime.toISOString());
          }
        }
        
        dispatch({ type: "LOGIN", payload: { user, role } });
        return { 
          success: true, 
          data: response.data 
        };
      }
      
      return { 
        success: false, 
        error: "Invalid response format" 
      };
    } catch (error) {
      const errorMessage = error?.response?.data?.message || "Login failed";
      dispatch({ type: "AUTH_ERROR", payload: { error: errorMessage } });
      
      return { 
        success: false, 
        error: errorMessage,
        status: error?.response?.status
      };
    }
  };

  // Admin Login
  const adminLogin = async (adminId, adminPassword) => {
    try {
      const response = await axiosApiInstance.post(ADMIN_LOGIN_URL, {
        adminId: adminId,
        adminPassword: adminPassword,
      });
      if (response?.data) {
        const { data: user } = response?.data || {};
        dispatch({ type: "ADMINLOGIN", payload: { user } });
        return { success: true, data: response.data };
      }
      return { success: false, error: "Invalid response format" };
    } catch (error) {
      const errorMessage = error?.response?.data?.message || "Admin login failed";
      dispatch({ type: "AUTH_ERROR", payload: { error: errorMessage } });
      return { 
        success: false, 
        error: errorMessage,
        status: error?.response?.status
      };
    }
  };

  // Register
  const register = async (email, username, password) => {
    try {
      const response = await axios.post("/api/auth/register", { email, username, password }, { withCredentials: true });
      dispatch({ type: "LOGIN", payload: { user: response.data.user, role: response.data.user.role } });
    } catch (error) {
      console.error("Registration Error:", error);
      throw error;
    }
  };

  // Logout
  const logout = async () => {
    try {
      dispatch({ type: "LOGOUT" });
      clearLoggedInUserInvalidCredential(navigate);
    } catch (error) {
      console.error("Logout Error:", error);
      dispatch({ type: "LOGOUT" });
      clearLoggedInUserInvalidCredential(navigate);
    }
  };

  // Check if route is public - FIXED VERSION
  const isPublicRoute = (pathname) => {
    const publicRoutes = [
      '/',
      '/activity-tracking',
      '/leave-management',
      '/real-time-insight',
      '/WFH-support',
      '/compliences-report',
      '/visitor-management',
      '/pricing',
      '/blogs',
      '/faq',
      '/contact-us',
      '/about-us',
      '/company-register',
      '/login',
      '/admin/login',
      '/forgot-password',
      '/admin-forgot-password',
      '/verify-email',
      '/offer'
    ];

    // Check exact match
    if (publicRoutes.includes(pathname)) {
      return true;
    }

    // Check if it's a blog route (starts with /blog-)
    if (pathname.startsWith('/blog-')) {
      return true;
    }

    // Check if it's a solution page (starts with /solutions/)
    if (pathname.startsWith('/solutions/')) {
      return true;
    }

    // Check if it's a client approval page
    if (pathname.startsWith('/client-approval')) {
      return true;
    }

    return false;
  };

  // Check token expiration
  const checkTokenExpiration = () => {
    const tokenExpiration = localStorage.getItem("tokenExpiration");
    const token = localStorage.getItem("tokan");
    if (!token || !tokenExpiration) {
      logout();
      return;
    }

    const expirationDate = new Date(tokenExpiration);
    const currentTime = new Date();

    if (expirationDate <= currentTime) {
      logout();
    }
  };

  // Re-validate role/module access from the server (TTE_0365) -- the JWT
  // keeps whatever roleKey it was issued with, so if an admin reassigns this
  // user's role after they've logged in, the token alone never picks it up
  // until they log out and back in. Piggyback on the same
  // interval/focus/visibility triggers used for token expiration below
  // instead of adding a websocket or a second timer.
  const refreshRole = async () => {
    const token = localStorage.getItem("tokan");
    if (!token) return;
    // System-admin sessions have no roleId/companyId (authMiddleware's
    // "admin" branch never sets them), so getCurrentRole structurally can't
    // succeed for them -- and there's no company-scoped role to drift out
    // of sync with anyway. Skip instead of polling a call that always fails.
    if (localStorage.getItem("admin") === "system") return;
    try {
      const res = await getCurrentRole();
      const freshRole = res?.data?.roleData;
      if (!freshRole) return;
      const storedRole = JSON.parse(localStorage.getItem("role") || "null");
      if (JSON.stringify(storedRole) !== JSON.stringify(freshRole)) {
        localStorage.setItem("role", JSON.stringify(freshRole));
        dispatch({ type: "REFRESH_ROLE", payload: { role: freshRole } });
      }
    } catch (error) {
      // Best-effort refresh -- a transient failure here shouldn't log the
      // user out; checkTokenExpiration already owns that decision.
    }
  };

  useEffect(() => {
    const publicRoute = isPublicRoute(location.pathname);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && !publicRoute) {
        checkTokenExpiration();
        refreshRole();
      }
    };

    const handleWindowFocus = () => {
      if (!publicRoute) {
        checkTokenExpiration();
        refreshRole();
      }
    };

    if (!publicRoute) {
      checkTokenExpiration();
      refreshRole();
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleWindowFocus);

    const interval = setInterval(() => {
      if (!publicRoute) {
        checkTokenExpiration();
        refreshRole();
      }
    }, 60 * 1000);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [location.pathname]);

  // Auto logout on API error
  const autoLogoutOnError = (err) => {
    const errorMessage = err?.response?.data?.message || "An error occurred!";
    console.error("API Error:", errorMessage);

    if (err?.response?.status === 401 || err?.response?.data?.message === "Auth Header missing!") {
      logout();
    } else {
      generateErrorMessage(errorMessage);
    }
  };

  // Check-in
  const checkIn = (checkIn) => dispatch({ type: "CHECK_IN", payload: { checkIn } });

  // Check-out
  const checkOut = (checkOut) => dispatch({ type: "CHECK_OUT", payload: { checkOut } });

  // Update username
  const updateUserName = (userName) => dispatch({ type: "UPDATE_USER_NAME", payload: { userName } });

  useEffect(() => {
    const handleAPIError = async (err) => {
      if (err) {
        autoLogoutOnError(err);
      }
    };
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, method: "JWT", login, logout, register, adminLogin, checkIn, checkOut, updateUserName }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;